import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Matches the absolute footer's vertical padding (2 × 16px in EntityDetail / EntityCrud SCSS).
 * Use as the additive when callers compute a declared inset from `bottomPanel.height`.
 */
export const ABSOLUTE_FOOTER_VERTICAL_PADDING = 32;

/**
 * Scroll inset for content above a position:absolute bottom bar (e.g. sales order totals).
 * `declaredInset` is a static estimate; measured DOM height wins when larger (wrapped lines, tax rows, etc.).
 */
export function useScrollInsetForAbsoluteFooter(hasFooter, declaredInset) {
    const footerRef = useRef(null);
    const [measuredHeight, setMeasuredHeight] = useState(0);

    useLayoutEffect(() => {
        if (!hasFooter) {
            setMeasuredHeight(0);
            return undefined;
        }

        const el = footerRef.current;
        if (!el) {
            setMeasuredHeight(0);
            return undefined;
        }

        const update = () => {
            setMeasuredHeight(Math.ceil(el.getBoundingClientRect().height));
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(el);

        return () => {
            ro.disconnect();
        };
    }, [hasFooter]);

    const inset = hasFooter ? Math.max(declaredInset || 0, measuredHeight) : 0;

    return { footerRef, inset };
}
